export type PCConsoleMetricCategory =
  | 'sales'
  | 'pcuAcu'
  | 'mau'
  | 'dau'
  | 'engagement'
  | 'wishlistAndFollows'
  | 'priceAndReviews'
  | 'retention'
  | 'noUpdate';

export type PCConsoleMetricType =
  | PCConsoleMetricCategory // PC&Console 通用展示分类
  | 'opinions'; // Compare 专用舆情分类，不进入九类通用分类

export const PC_CONSOLE_DISPLAY_CATEGORY_ORDER: PCConsoleMetricCategory[] = [
  'sales',
  'pcuAcu',
  'mau',
  'dau',
  'engagement',
  'wishlistAndFollows',
  'priceAndReviews',
  'retention',
  'noUpdate',
];

export const PC_CONSOLE_DATA_SOURCE_ORDER: Record<string, number> = {
  databrain: 0,
  mscience: 1,
  gsd: 2,
  source_steam: 3,
  steam: 3,
  steamcharts: 3,
  alinea: 4,
  ampere: 5,
  gamalytic: 6,
  vginsights: 7,
  npd: 8,
  stream: 9,
  streamhatchet: 9,
  newzoo: 10,
  opinions: 11,
};

export const getPCConsoleSourceOrder = (source?: string): number =>
  PC_CONSOLE_DATA_SOURCE_ORDER[String(source || '').toLowerCase()] ?? Number.MAX_SAFE_INTEGER;

type PCConsoleSourceGroup = {
  value?: string;
  tableSource?: string;
  dataSource?: string;
};

const getPCConsoleSourceGroupKey = (group: PCConsoleSourceGroup): string =>
  group.tableSource || group.dataSource || group.value || '';

/**
 * 页面 Source、下载弹窗 Source group 共用同一套优先级，但各自保留原有指标内容定义。
 * 未配置的数据源排在末尾，同优先级时保持原数组顺序，避免改变同源指标的既有排列。
 */
export const orderPCConsoleSourceGroups = <T extends PCConsoleSourceGroup>(
  groups: T[] = [],
): T[] =>
  groups
    .map((group, index) => ({ group, index }))
    .sort((left, right) => {
      const orderDiff =
        getPCConsoleSourceOrder(getPCConsoleSourceGroupKey(left.group)) -
        getPCConsoleSourceOrder(getPCConsoleSourceGroupKey(right.group));
      return orderDiff || left.index - right.index;
    })
    .map(({ group }) => group);

type PCConsoleDisplayCategoryFields = {
  displayCategoryLocaleKey: string;
  categoryDisabledTipsLocaleKey: string;
};

/**
 * 一级分类统一保存 locale id；交互式筛选项交给 formatMessage，级联配置在聚合时从中英文 locale 字典解析，避免维护两份文案。
 */
export const PC_CONSOLE_DISPLAY_CATEGORY_FIELDS: Record<string, PCConsoleDisplayCategoryFields> = {
  sales: {
    displayCategoryLocaleKey: 'dbi_pc_console_metric_type_sales',
    categoryDisabledTipsLocaleKey: 'dbi_info_revenue_metrics_category_disabled_tips',
  },
  pcuAcu: {
    displayCategoryLocaleKey: 'dbi_pc_console_metric_type_pcu_acu',
    categoryDisabledTipsLocaleKey: 'dbi_info_steam_data_metrics_category_disabled_tips',
  },
  mau: {
    displayCategoryLocaleKey: 'dbi_pc_console_metric_type_mau',
    categoryDisabledTipsLocaleKey: 'dbi_info_ampere_mau_category_disabled_tips',
  },
  dau: {
    displayCategoryLocaleKey: 'dbi_pc_console_metric_type_dau',
    categoryDisabledTipsLocaleKey: 'dbi_info_ampere_dau_category_disabled_tips',
  },
  engagement: {
    displayCategoryLocaleKey: 'dbi_pc_console_metric_type_engagement',
    categoryDisabledTipsLocaleKey: 'dbi_info_ampere_engagement_category_disabled_tips',
  },
  wishlistAndFollows: {
    displayCategoryLocaleKey: 'dbi_pc_console_metric_type_wishlist_follows',
    categoryDisabledTipsLocaleKey: 'dbi_info_steam_data_metrics_category_disabled_tips',
  },
  priceAndReviews: {
    displayCategoryLocaleKey: 'dbi_pc_console_metric_type_price_reviews',
    categoryDisabledTipsLocaleKey: 'dbi_info_steam_data_metrics_category_disabled_tips',
  },
  retention: {
    displayCategoryLocaleKey: 'dbi_pc_console_metric_type_retention',
    categoryDisabledTipsLocaleKey: 'dbi_info_ampere_retention_category_disabled_tips',
  },
  noUpdate: {
    displayCategoryLocaleKey: 'dbi_pc_console_metric_type_no_update',
    categoryDisabledTipsLocaleKey: 'dbi_info_newzoo_noupdate_metrics_category_disabled_tips',
  },
};

export const PC_CONSOLE_METRIC_TYPE_OPTIONS = PC_CONSOLE_DISPLAY_CATEGORY_ORDER.map((value) => ({
  text: PC_CONSOLE_DISPLAY_CATEGORY_FIELDS[value].displayCategoryLocaleKey,
  value,
}));

/**
 * 仅展示当前日期粒度确实存在指标的分类。
 * DAU/MAU 等粒度受限分类因此不会在 weekly 中出现；指标 value 和请求参数不参与转换。
 */
export const filterPCConsoleMetricTypeOptionsByMetrics = (
  options: Array<{ text: any; value: string; }> = [],
  metrics: any[] = [],
) =>
  options.filter((option) =>
    metrics.some((metric) => {
      return getPCConsoleMetricDisplayCategory(metric) === option.value;
    }),
  );

export const PC_CONSOLE_SOURCE_OPTIONS = orderPCConsoleSourceGroups([
  { text: 'DataBrain', value: 'databrain', icon: 'databrainbluebg' },
  { text: 'MScience', value: 'mscience', icon: 'mscience' },
  { text: 'GSD', value: 'gsd', icon: 'gsd' },
  { text: 'Steam', value: 'source_steam', icon: 'source_steam' },
  { text: 'Alinea Analytics', value: 'alinea', icon: 'alinea' },
  { text: 'dbi_other_vendor', value: 'ampere', icon: 'ampere' },
  { text: 'Gamalytic', value: 'gamalytic', icon: 'gamalytic' },
  { text: 'VGI', value: 'vginsights', icon: 'vginsights' },
  { text: 'NPD', value: 'npd', icon: 'npd' },
  { text: 'dbi_pc_console_metric_type_no_update', value: 'newzoo', icon: 'newzoo' },
]);

export const PC_CONSOLE_COMPARE_SOURCE_OPTIONS = orderPCConsoleSourceGroups([
  ...PC_CONSOLE_SOURCE_OPTIONS,
  { text: 'Opinions', value: 'opinions' },
]);

const DISPLAY_CATEGORY_BY_SELECTOR: Record<string, PCConsoleMetricCategory> = {
  'steamData###source_steam__pcu': 'pcuAcu',
  'steamData###source_steam__acu': 'pcuAcu',
  'steamData###pcu': 'pcuAcu',
  'steamData###acu': 'pcuAcu',
  'steamData###steamfollowers': 'wishlistAndFollows',
  'steamData###wishlists': 'wishlistAndFollows',
  'steamData###wishlists_total': 'wishlistAndFollows',
  'steamData###est_price': 'priceAndReviews',
  'steamData###steamreviews': 'priceAndReviews',
  'steamData###playtime': 'engagement',
  'gamalytic###gamalytic__pcu': 'pcuAcu',
  'gamalytic###gamalytic__acu': 'pcuAcu',
  'gamalytic###gamalytic__steamfollowers': 'wishlistAndFollows',
  'gamalytic###gamalytic__wishlists': 'wishlistAndFollows',
  'gamalytic###gamalytic__wishlists_total': 'wishlistAndFollows',
  'gamalytic###gamalytic__est_price': 'priceAndReviews',
  'gamalytic###gamalytic__weighted_price': 'priceAndReviews',
  'gamalytic###gamalytic__steamreviews': 'priceAndReviews',
  'gamalytic###gamalytic__cumulative_reviews': 'priceAndReviews',
  'gamalytic###gamalytic__playtime': 'engagement',
  'alinea###alinea__pcu': 'pcuAcu',
  'alinea###alinea__acu': 'pcuAcu',
  'alinea###alinea__steamfollowers': 'wishlistAndFollows',
  'alinea###alinea__wishlists': 'wishlistAndFollows',
  'alinea###alinea__wishlists_total': 'wishlistAndFollows',
  'alinea###alinea__playtime': 'engagement',
  'alinea###alinea__est_price': 'priceAndReviews',
  'alinea###alinea__steamreviews': 'priceAndReviews',
  'alinea###alinea__cumulative_reviews': 'priceAndReviews',
  'alinea###alinea__positive': 'priceAndReviews',
  'alinea###alinea__cumulative_positive': 'priceAndReviews',
  'alinea###alinea__players': 'engagement',
  'alinea###alinea__players_total': 'engagement',
  'alinea###alinea__dau': 'dau',
  'alinea###alinea__mau': 'mau',
  'consoleData###alinea__ps_dau': 'dau',
  'consoleData###alinea__xbox_dau': 'dau',
  'consoleData###alinea__ps_mau': 'mau',
  'consoleData###alinea__xbox_mau': 'mau',
  'consoleData###alinea__ps_players': 'engagement',
  'consoleData###alinea__ps_players_total': 'engagement',
  'consoleData###alinea__ps_game_pass_players': 'engagement',
  'consoleData###alinea__ps_game_pass_players_total': 'engagement',
  'consoleData###alinea__xbox_players': 'engagement',
  'consoleData###alinea__xbox_players_total': 'engagement',
  'consoleData###alinea__xbox_game_pass_players': 'engagement',
  'consoleData###alinea__xbox_game_pass_players_total': 'engagement',
  'consoleData###alinea__ps_price': 'priceAndReviews',
  'consoleData###alinea__xbox_price': 'priceAndReviews',
};

export const getPCConsoleDisplayCategory = (category: string, metric = '') => {
  if (category === 'revenue') return 'sales';
  if (['mau', 'dau', 'engagement', 'retention', 'noUpdate'].includes(category)) return category;
  return (
    DISPLAY_CATEGORY_BY_SELECTOR[`${category}###${metric}`] ||
    getPCConsoleMetricDisplayCategory({ value: metric })
  );
};

/**
 * 将不同选择器的旧分类字段统一解析为 PC&Console 的展示分类。
 * `metricsType` 用于 KPI/Compare，`segType` 用于 Stats；两者都只参与 UI 筛选，
 * 不会写入后端请求，因此可以在这里转换而不改变稳定 metric value。
 */
export const getPCConsoleMetricDisplayCategory = (
  metric: any,
): PCConsoleMetricType | undefined => {
  if (!metric) return undefined;
  if (metric?.displayCategory) return metric.displayCategory;

  const value = String(metric?.value || '').toLowerCase();
  const legacyType = String(metric?.metricsType || metric?.segType || '').toLowerCase();
  const dataSource = String(metric?.dataSource || metric?.dataSourceType || '').toLowerCase();

  // Opinions 是 Compare 专用类型，不属于九个 PC&Console 标准分类；必须原样返回以匹配其指标项。
  if (legacyType === 'opinions' || dataSource === 'opinions') return 'opinions';
  // 复用选择器的既有分类配置，避免 All Metrics 再通过 value 命名猜测同一指标的业务分类。
  const selectorCategory = DISPLAY_CATEGORY_BY_SELECTOR[`${dataSource}###${value}`];
  if (selectorCategory) return selectorCategory;
  if (legacyType === 'noupdate' || dataSource === 'newzoo') return 'noUpdate';
  if (legacyType === 'retention' || /(?:^|_)(?:d1|d7|d14|d28|d60|rolling_d1|rolling_d7|rolling_d14|rolling_d28|rolling_d60)$/.test(value)) {
    return 'retention';
  }
  if (legacyType === 'mau' || value.endsWith('_mau')) return 'mau';
  if (legacyType === 'dau' || value.endsWith('_dau')) return 'dau';
  if (['steamfollowers', 'wishlists', 'wishlists_total'].includes(value) || value.includes('steamfollowers') || value.includes('wishlists')) {
    return 'wishlistAndFollows';
  }
  if (
    legacyType === 'price' ||
    value.includes('est_price') ||
    value.endsWith('_price') ||
    value.includes('weighted_price') ||
    value.includes('steamreviews') ||
    value === 'meta_score'
  ) {
    return 'priceAndReviews';
  }
  if (legacyType === 'pcu' || value.endsWith('__pcu') || value.endsWith('__acu') || value === 'pcu' || value === 'acu') {
    return 'pcuAcu';
  }
  if (
    legacyType === 'playtime' ||
    legacyType === 'engagement' ||
    legacyType === 'ampere' ||
    legacyType === 'churnacq' ||
    legacyType === 'timespent' ||
    dataSource === 'ampere' ||
    value.includes('playtime') ||
    value.includes('players')
  ) {
    return 'engagement';
  }
  if (
    ['revenue', 'units', 'arpu'].includes(legacyType) ||
    value.includes('revenue') ||
    value.includes('units') ||
    value.includes('arppu') ||
    value === 'ast'
  ) {
    return 'sales';
  }

  // 未识别项不自动归入 Sales，调用方可保留原 category；测试会阻止新的 PC&Console 指标漏配。
  return undefined;
};

export const normalizePCConsoleMetricType = (value?: string): PCConsoleMetricType | string => {
  const normalizedValue = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '');
  const legacyMap: Record<string, PCConsoleMetricType | 'all'> = {
    revenue: 'sales',
    units: 'sales',
    arpu: 'sales',
    pcu: 'pcuAcu',
    pcuacu: 'pcuAcu',
    activeuser: 'pcuAcu',
    mau: 'mau',
    dau: 'dau',
    playtime: 'engagement',
    ampere: 'engagement',
    engagement: 'engagement',
    churnacq: 'engagement',
    timespent: 'engagement',
    steamfollowers: 'wishlistAndFollows',
    wishlistandfollows: 'wishlistAndFollows',
    price: 'priceAndReviews',
    priceandreviews: 'priceAndReviews',
    retention: 'retention',
    noUpdate: 'noUpdate',
    noupdate: 'noUpdate',
    // Opinions 只允许通过 Source 筛选；历史保存的 Type 值恢复为 All，避免出现不存在的 Type 选项。
    opinions: 'all',
  };
  return legacyMap[normalizedValue] || value?.trim() || '';
};

/**
 * 只为 PC&Console 详情选择器生成排序后的派生列表。
 * 不改写 allMetricsOptions，避免下载、表格和 Mobile 继续依赖的历史数组顺序被改变。
 * Metrics 先按数据源优先级，再按分类顺序；同一数据源和分类内保留原定义顺序。
 */
type PCConsoleMetricSourceField = 'dataSourceType' | 'dataSource' | 'displaySource' | 'icon';

/**
 * 按数据源、展示分类和原始定义顺序排列指标。
 * 默认沿用 All Metrics 的 dataSourceType 分组；详情页可以使用 displaySource 表示页面展示数据源，
 * 因此调用方可以显式指定 sourceField，避免把同一展示数据源拆成多个分组。
 */
export const orderPCConsoleMetrics = <T extends Record<string, any>>(
  metrics: T[] = [],
  sourceField: PCConsoleMetricSourceField = 'dataSourceType',
): T[] =>
  metrics
    .map((metric, index) => ({ metric, index }))
    .sort((left, right) => {
      const categoryOrder = (metric: T) => {
        const category = getPCConsoleMetricDisplayCategory(metric);
        const order = PC_CONSOLE_DISPLAY_CATEGORY_ORDER.indexOf(category as PCConsoleMetricCategory);
        return order < 0 ? Number.MAX_SAFE_INTEGER : order;
      };
      const leftSource = left.metric?.[sourceField] || left.metric?.dataSource || left.metric?.dataSourceType;
      const rightSource = right.metric?.[sourceField] || right.metric?.dataSource || right.metric?.dataSourceType;
      const sourceDiff = getPCConsoleSourceOrder(leftSource) - getPCConsoleSourceOrder(rightSource);
      if (sourceDiff !== 0) return sourceDiff;
      const categoryDiff = categoryOrder(left.metric) - categoryOrder(right.metric);
      return categoryDiff !== 0 ? categoryDiff : left.index - right.index;
    })
    .map(({ metric }) => metric);
